import {
  Container,
  TypographyElement,
  Skeleton,
  Icon,
  Icons,
  Row,
  AdCard,
  Col
} from 'UI'
import Text from '../text.json'
import { CatalogLearningPathsPath, CatalogPath, CourseNeutralPath } from 'navigation/Routes'
import { createUseStyles, useTheme } from 'react-jss'
import style from './style'
import { ExternalAlphormPages, showLearningPath } from 'constants/'
import CoursesGridNew from 'components/common/CoursesGridNew'
import { SelectItemDataLayer } from 'helpers'
import LearningPathsGrid from 'components/common/LearningPathsGrid'
import { decorateUrl } from 'components/common/UTMscript'

const useStyle = createUseStyles(style)

const ResultInfos = ({ courses, learningPaths }) => {
  const { iconClass, resultInfos } = useStyle()
  return (
    <div className={resultInfos}>
      <Icon iconName={Icons.path} style={iconClass} />
      <TypographyElement
        component='h4'
        variant='body1'
        spacing='0 23px 0 0'
      >
        {`${learningPaths.length} ${Text.learningPaths}`}
      </TypographyElement>
      <Icon iconName={Icons.video} style={iconClass} />
      <TypographyElement
        component='h4'
        variant='body1'
        spacing='0 16px 0 0'
      >
        {`${courses.length} ${Text.courses}`}
      </TypographyElement>
    </div>
  )
}

const LearningPathsComponent = ({ loading, learningPaths }) => {
  return (
    <>
      <TypographyElement
        component='h2'
        variant='heading2'
        spacing='0 0 24px 0'
      >
        {Text.learningPathResults}
      </TypographyElement>
      <LearningPathsGrid
        loading={loading}
        learningPaths={learningPaths}
        columnGap={43}
        tabletColumnGap={24}
        mobileColumnGap={0}
        rowGap={24}
        tabletRowGap={24}
        mobileRowGap={24}
        columnCount={4}
        tabletColumnCount={2}
        mobileColumnCount={1}
        containerMarginBottom={64}
        containerMarginBottomTablet={32}
        containerMarginBottomMobile={32}
      />
    </>
  )
}

const CoursesComponent = ({ loading, courses, goToLink, searchTerm }) => {
  return (
    <>
      <TypographyElement
        component='h2'
        variant='heading2'
        spacing='0 0 24px 0'
      >
        {Text.courses}
      </TypographyElement>
      <CoursesGridNew
        loading={loading}
        courses={courses}
        columnGap={43}
        tabletColumnGap={24}
        mobileColumnGap={0}
        rowGap={24}
        tabletRowGap={24}
        mobileRowGap={24}
        columnCount={4}
        tabletColumnCount={2}
        mobileColumnCount={1}
        containerMarginBottom={64}
        containerMarginBottomTablet={32}
        containerMarginBottomMobile={32}
        handleCardClick={(course) => {
          SelectItemDataLayer({ item: course, listName: searchTerm, catalog: true })
          goToLink(`${CourseNeutralPath}/${course.Slug}`)
        }}
      />
    </>
  )
}

export default ({ courses, learningPaths, searchTerm, goToLink, loading, Tag }) => {
  const theme = useTheme()
  const { advertisementContainer } = useStyle()
  const introduction = Tag?.Introduction || ''
  const title = Tag?.AlternativeTitle || Tag?.Name

  return (
    <Container>
      <TypographyElement Component='h1' variant='heading1' margin='64px 0 0 0' spacing='0 0 16px'>
        {`${title}`}
      </TypographyElement>
      <TypographyElement Component='p' spacing='0 0 5px 0'>
        {`${introduction}`}
      </TypographyElement>
      {
        loading
          ? <Skeleton width='30%' height={26} margin='0 0 32px 0' />
          : <ResultInfos courses={courses} learningPaths={learningPaths} />
      }
      {
        loading || (courses.length || learningPaths.length)
          ? (
            <>
              {courses.length ? <CoursesComponent searchTerm={searchTerm} loading={loading} courses={courses} goToLink={goToLink} /> : null}
              {learningPaths.length && showLearningPath ? <LearningPathsComponent loading={loading} learningPaths={learningPaths} /> : null}
            </>
            )
          : (
            <TypographyElement
              variant='body1'
              spacing='0 0 32px 0'
              Component='h2'
            >
              {`${Text.noResults} "${searchTerm}". ${Text.checkCategories}`}
            </TypographyElement>
            )
      }
      {
        loading || (courses.length && learningPaths.length)
          ? (
            <TypographyElement
              variant='heading1'
              spacing='0 0 24px 0'
            >
              {Text.advertisementTitle}
            </TypographyElement>
            )
          : null
      }
      <Row
        direction='row'
        tabletDirection='column'
        mobileDirection='column'
        className={advertisementContainer}
      >
        <Col grid={4} mgrid={12} sgrid={12}>
          <AdCard
            title={Text.recommendNewCourses.title}
            core={Text.recommendNewCourses.paragraph}
            buttonLabel={Text.recommendNewCourses.button}
            onButtonClick={() => window.open(ExternalAlphormPages.NewCoursesIdeas, '_blank', 'noopener')}
            shadowColor={theme.secondary[0]}
            tabletMargin='0 0 16px 0'
            mobileMargin='0 0 16px 0'
            href={decorateUrl(ExternalAlphormPages.NewCoursesIdeas)}
          />
        </Col>
        <Col grid={4} mgrid={12} sgrid={12}>
          <AdCard
            title={Text.courseCard.title}
            core={Text.courseCard.paragraph}
            buttonLabel={Text.courseCard.button}
            onButtonClick={() => goToLink(CatalogPath)}
            shadowColor={theme.primary[0]}
            tabletMargin='0 0 16px 0'
            mobileMargin='0 0 16px 0'
            href={decorateUrl(CatalogPath)}
          />
        </Col>
        <Col grid={4} mgrid={12} sgrid={12}>
          <AdCard
            title={Text.learningPathCard.title}
            core={Text.learningPathCard.paragraph}
            buttonLabel={Text.learningPathCard.button}
            onButtonClick={() => goToLink(CatalogLearningPathsPath)}
            shadowColor={theme.extra.violet}
            href={decorateUrl(CatalogLearningPathsPath)}
          />
        </Col>
      </Row>
    </Container>
  )
}
